import {
    type MembershipBillingFrequency,
    type StripeSubscriptionSummary,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarMonths } from 'date-fns';
import { Link } from 'expo-router';
import * as React from 'react';
import { useEffect } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    InfoIcon,
    Sheet,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import { BILLING_FREQUENCY_INTERVAL_MONTHS } from '../../billing/constants/premiumPricingCopy';
import { useSetAutoRenew } from '../../billing/hooks/useAutoRenew';
import { centsToDollars, formatDollars } from '../../common/utils/bankingUtils';
import {
    convertToDate,
    format,
    formatReadableMonthDay,
    SHORT_READABLE_DATE_FORMAT,
} from '../../common/utils/dateUtils';
import {
    SUCCESS_TOAST_DURATION,
    SuccessToastIcon,
} from '../../member-profile/components/SuccessToast';

const getRemainingPaymentsCount = (
    currentPeriodEnd: string,
    switchAt: string,
    billingFrequency: MembershipBillingFrequency | undefined,
): number => {
    if (billingFrequency == null) {
        return 0;
    }
    const intervalMonths = BILLING_FREQUENCY_INTERVAL_MONTHS[billingFrequency];
    const monthsRemaining = differenceInCalendarMonths(
        convertToDate(switchAt),
        convertToDate(currentPeriodEnd),
    );
    if (monthsRemaining < 0) {
        return 0;
    }
    return Math.round(monthsRemaining / intervalMonths);
};

const getPaymentsPerYear = (
    billingFrequency: MembershipBillingFrequency | undefined,
): number => {
    if (billingFrequency == null) {
        return 1;
    }
    return Math.round(12 / BILLING_FREQUENCY_INTERVAL_MONTHS[billingFrequency]);
};

interface AutoRenewToggleSheetProps {
    isOpen: boolean;
    onClose: () => void;
    enabled: boolean;
    copyVariant: 'premium' | 'legacy';
    userId: string;
    subscription: StripeSubscriptionSummary;
}

const getToastContent = (
    enabled: boolean,
    copyVariant: 'premium' | 'legacy',
    subscription: StripeSubscriptionSummary,
): { title: string; description: string; icon: React.ReactNode } => {
    const anniversaryDate = format(
        subscription.membershipYearEndsAt ?? subscription.renewalDate,
        SHORT_READABLE_DATE_FORMAT,
    );

    if (enabled) {
        return {
            title: 'Auto-renew turned on',
            description: `Premium renews ${anniversaryDate}`,
            icon: SuccessToastIcon,
        };
    }

    return {
        title: 'Auto-renew turned off',
        description:
            copyVariant === 'premium'
                ? `Premium ends ${anniversaryDate}`
                : `Paid benefits end ${anniversaryDate}`,
        icon: <Icon icon={InfoIcon} size={24} color="$gray1100" />,
    };
};

const getBodyCopy = (
    enabled: boolean,
    copyVariant: 'premium' | 'legacy',
    subscription: StripeSubscriptionSummary,
): string => {
    const autoRenewEndDate =
        subscription.membershipYearEndsAt ?? subscription.renewalDate;
    const anniversaryDate = formatReadableMonthDay(autoRenewEndDate);

    if (enabled) {
        const paymentsPerYear = getPaymentsPerYear(
            subscription.billingFrequency,
        );
        const paymentWord = paymentsPerYear === 1 ? 'payment' : 'payments';
        return `Premium will renew on ${anniversaryDate} for another year, billed as ${paymentsPerYear} ${paymentWord} of ${formatDollars(
            centsToDollars(subscription.amountCents),
            false,
        )}.`;
    }

    if (copyVariant === 'premium') {
        const remainingPayments = getRemainingPaymentsCount(
            subscription.renewalDate,
            autoRenewEndDate,
            subscription.billingFrequency,
        );
        const premiumEndDate = format(
            autoRenewEndDate,
            SHORT_READABLE_DATE_FORMAT,
        );
        const benefitsEndCopy = `Premium benefits end on ${premiumEndDate}, but your Fruitful membership and system stay active.`;

        if (remainingPayments <= 0) {
            return `You won't be billed again. ${benefitsEndCopy}`;
        }
        const frequency = (subscription.billingFrequency ?? '').toLowerCase();
        const paymentWord = remainingPayments === 1 ? 'payment' : 'payments';
        const verb = remainingPayments === 1 ? 'continues' : 'continue';
        return `Your remaining ${remainingPayments} ${frequency} ${paymentWord} ${verb} as scheduled. ${benefitsEndCopy}`;
    }
    return `You will no longer be billed. Your access to additional paid benefits will end on ${anniversaryDate}, but your Fruitful membership and system will remain active.`;
};

export const AutoRenewToggleSheet = ({
    isOpen,
    onClose,
    enabled,
    copyVariant,
    userId,
    subscription,
}: AutoRenewToggleSheetProps) => {
    const queryClient = useQueryClient();
    const apply = useSetAutoRenew();
    const toast = useToastController();
    const isPremiumTurnOff = !enabled && copyVariant === 'premium';

    useEffect(() => {
        if (!isOpen) {
            apply.reset();
        }
    }, [isOpen]);

    const handleConfirm = async () => {
        await apply.mutateAsync({ body: { enabled } });
        void queryClient.invalidateQueries({
            queryKey: [`getSubscription/${userId}`],
        });
        const { title, description, icon } = getToastContent(
            enabled,
            copyVariant,
            subscription,
        );
        toast.show(title, {
            description,
            icon,
            duration: SUCCESS_TOAST_DURATION,
        });
        onClose();
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            dismissOnOverlayPress={!apply.isPending}
            title={enabled ? 'Turn on auto-renew?' : 'Turn off auto-renew?'}
            showCloseButton={!apply.isPending}
        >
            <YStack
                paddingTop={8}
                width="100%"
                maxWidth={500}
                alignSelf="center"
                gap={24}
            >
                <BodyMediumRegular
                    color={isPremiumTurnOff ? '$gray1000' : '$gray900'}
                    textAlign={isPremiumTurnOff ? 'center' : undefined}
                >
                    {getBodyCopy(enabled, copyVariant, subscription)}
                    {isPremiumTurnOff && (
                        <>
                            {' '}
                            <Link
                                href="https://www.fruitful.com/pricing"
                                target="_blank"
                            >
                                <BodyMediumRegular
                                    color="$gray1000"
                                    textDecorationLine="underline"
                                >
                                    View membership benefits
                                </BodyMediumRegular>
                            </Link>
                            .
                        </>
                    )}
                </BodyMediumRegular>
                {apply.error != null && (
                    <BodySmallRegular color="$red1100">
                        Something went wrong. Please try again.
                    </BodySmallRegular>
                )}
                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PREMIUM}
                        size={ButtonSize.LARGE}
                        text={
                            apply.isPending
                                ? ''
                                : enabled
                                  ? 'Yes, turn on auto-renew'
                                  : 'Yes, turn off auto-renew'
                        }
                        icon={apply.isPending ? <Spinner /> : null}
                        onPress={() => {
                            void handleConfirm();
                        }}
                        disabled={apply.isPending}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text={enabled ? 'Go back' : 'Cancel'}
                        onPress={onClose}
                        disabled={apply.isPending}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};
